import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { SearchableSelect } from '../components/ui/searchable-select';
import { toast } from 'sonner';
import { IndianRupee, Wallet, CheckCircle2, Clock, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MaterialRequestCreatePage() {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [formData, setFormData] = useState({
    quote_id: '',
    remarks: ''
  });
  const [loading, setLoading] = useState(false);
  const [budgetInfo, setBudgetInfo] = useState(null);
  const [loadingBudget, setLoadingBudget] = useState(false);

  useEffect(() => {
    fetchQuotes();
  }, []);

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(`${API}/quotes`);
      setQuotes(response.data);
    } catch (error) {
      toast.error('Failed to load quotes');
    }
  };

  const fetchBudgetInfo = async (quoteId) => {
    if (!quoteId) {
      setBudgetInfo(null);
      return;
    }
    
    setLoadingBudget(true);
    try {
      const response = await axios.get(`${API}/quotes/${quoteId}/material-budget`);
      setBudgetInfo(response.data);
    } catch (error) {
      console.error('Failed to load budget info', error);
      setBudgetInfo(null);
    } finally {
      setLoadingBudget(false);
    }
  };

  const handleQuoteChange = (value) => {
    setFormData({ ...formData, quote_id: value });
    fetchBudgetInfo(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('quote_id', formData.quote_id);
      formDataToSend.append('remarks', formData.remarks);

      await axios.post(`${API}/material-requests`, formDataToSend);
      toast.success('Material request created successfully');
      navigate('/material-requests');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create material request');
    } finally {
      setLoading(false);
    }
  };

  const selectedQuote = quotes.find(q => q.id === formData.quote_id);

  // Prepare quote options for searchable select
  const quoteOptions = quotes.map((quote) => ({
    value: quote.id,
    label: `${quote.quote_no} - ${(quote.work_description || 'No description').substring(0, 50)}${(quote.work_description || '').length > 50 ? '...' : ''}`,
    quote_no: quote.quote_no,
    work_description: quote.work_description
  }));

  return (
    <Sidebar>
      <div className="space-y-6 max-w-2xl" data-testid="material-request-create-page">
        <div>
          <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Create Material Request
          </h1>
          <p className="text-muted-foreground mt-2">
            Request materials for an assigned quote
          </p>
        </div>

        <Card className="p-6 glass-card">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="quote">Select Quote *</Label>
              <SearchableSelect
                options={quoteOptions}
                value={formData.quote_id}
                onValueChange={handleQuoteChange}
                placeholder="Search and select a quote..."
                searchPlaceholder="Search by quote number or description..."
                emptyText="No quotes found."
                getOptionValue={(opt) => opt.value}
                getOptionLabel={(opt) => opt.label}
                data-testid="quote-select"
              />
            </div>

            {/* Budget Information Card */}
            {formData.quote_id && (
              <div className="p-4 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200">
                <h3 className="font-semibold text-sm text-blue-800 mb-3 flex items-center gap-2">
                  <Wallet className="h-4 w-4" />
                  Quote Budget Information
                </h3>
                
                {loadingBudget ? (
                  <div className="text-sm text-blue-600 animate-pulse">Loading budget details...</div>
                ) : budgetInfo ? (
                  <div className="space-y-3">
                    {/* Quote Details */}
                    {selectedQuote && (
                      <div className="text-xs text-gray-600 pb-2 border-b border-blue-200">
                        <span className="font-medium">Quote No:</span> {selectedQuote.quote_no}
                      </div>
                    )}
                    
                    <div className="grid grid-cols-2 gap-3">
                      {/* Total Quote Amount */}
                      <div className="p-3 bg-white/70 rounded-lg">
                        <div className="text-xs text-gray-500 mb-1">Total Quote Amount</div>
                        <div className="text-lg font-bold text-gray-800 flex items-center">
                          <IndianRupee className="h-4 w-4" />
                          {budgetInfo.total_budget?.toLocaleString('en-IN') || '0'}
                        </div>
                      </div>
                      
                      {/* Already Approved */}
                      <div className="p-3 bg-white/70 rounded-lg">
                        <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                          <CheckCircle2 className="h-3 w-3 text-green-600" />
                          Already Approved
                        </div>
                        <div className="text-lg font-bold text-red-600 flex items-center">
                          <IndianRupee className="h-4 w-4" />
                          {budgetInfo.total_already_approved?.toLocaleString('en-IN') || '0'}
                        </div>
                        {(budgetInfo.fund_approved > 0 || budgetInfo.dm_approved > 0) && (
                          <div className="text-[10px] text-gray-500 mt-1">
                            {budgetInfo.fund_approved > 0 && <div>Fund: Rs.{budgetInfo.fund_approved?.toLocaleString('en-IN')}</div>}
                            {budgetInfo.dm_approved > 0 && <div>Material: Rs.{budgetInfo.dm_approved?.toLocaleString('en-IN')}</div>}
                          </div>
                        )}
                      </div>
                      
                      {/* Pending Approval */}
                      <div className="p-3 bg-white/70 rounded-lg">
                        <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                          <Clock className="h-3 w-3 text-yellow-600" />
                          Pending Approval
                        </div>
                        <div className="text-lg font-bold text-yellow-700 flex items-center">
                          <IndianRupee className="h-4 w-4" />
                          {budgetInfo.total_pending?.toLocaleString('en-IN') || '0'}
                        </div>
                      </div>
                      
                      {/* Available Budget */}
                      <div className="p-3 bg-white/70 rounded-lg border-2 border-blue-300">
                        <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3 text-blue-600" />
                          Available Budget
                        </div>
                        <div className={`text-lg font-bold flex items-center ${budgetInfo.available_budget > 0 ? 'text-blue-700' : 'text-red-600'}`}>
                          <IndianRupee className="h-4 w-4" />
                          {budgetInfo.available_budget?.toLocaleString('en-IN') || '0'}
                        </div>
                      </div>
                    </div>
                    
                    {budgetInfo.available_budget <= 0 && (
                      <div className="text-xs text-red-600 bg-red-50 p-2 rounded-lg mt-2">
                        Warning: No budget available for this quote. Material request may be rejected.
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500">Unable to load budget information</div>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="remarks">Remarks * <span className="text-xs text-muted-foreground">({formData.remarks.length}/500)</span></Label>
              <Textarea
                id="remarks"
                placeholder="Provide details about the material request..."
                value={formData.remarks}
                onChange={(e) => {
                  if (e.target.value.length <= 500) {
                    setFormData({ ...formData, remarks: e.target.value });
                  }
                }}
                required
                maxLength={500}
                rows={4}
                data-testid="remarks-input"
              />
            </div>

            <div className="flex gap-3">
              <Button type="submit" disabled={loading} data-testid="submit-button">
                {loading ? 'Submitting...' : 'Submit Request'}
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate('/material-requests')}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </Sidebar>
  );
}
